import { Modal, Form, Select, Radio, DatePicker, message, Button } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useMemo } from 'react';
import {
  apiV2ExportCenterTaskAreaMeterReadingCreatePost,
  apiV2MeterReadingCategoryAreaListPost,
  apiV2MeterReadingExecutePost,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { DoubleRightOutlined } from '@ant-design/icons';

export interface ExportModalProps {
  visible: boolean;
  onCancel: () => void;
  modalMode?: string; // 导出模式或重新抄表模式
  updateFn: () => void;
}

export interface ScadaOption {
  id?: number;
  name?: string;
}

interface ExportFormValues {
  scadaIds: number[];
  modalMode?: number[];
  granularityType?: 'HOUR' | 'DAY' | 'MONTH';
  readingTime?: [Dayjs, Dayjs];
}

const ExportModal: FC<ExportModalProps> = ({ visible, onCancel, modalMode, updateFn }) => {
  const [form] = Form.useForm();

  const granularityType = Form.useWatch('granularityType', form);

  const isReReadMode = modalMode === 'reRead';

  // 时间颗粒度对应的最大范围（天）
  const maxRangeMap: Record<string, number> = {
    HOUR: 7,
    DAY: 31,
    MONTH: 365,
  };

  // 禁用超出范围的日期
  const disabledDate = (current: Dayjs) => {
    const maxRange = maxRangeMap[granularityType];
    const startDate = current?.add(-maxRange, 'day');
    return current && (current > dayjs().endOf('day') || (startDate && startDate > dayjs().endOf('day')));
  };

  const { data: areaListData, loading: areaListLoading } = useRequest(apiV2MeterReadingCategoryAreaListPost, {
    ready: visible,
    refreshDeps: [visible],
  });

  // 处理导出
  const handleFinish = (values: ExportFormValues) => {
    const { granularityType: granularityTypeFromForm, scadaIds, readingTime } = values;
    const isMonth = (granularityTypeFromForm || granularityType) === 'MONTH';

    if (isReReadMode) {
      // 重新抄表模式
      if (!readingTime?.[0] || !readingTime?.[1]) {
        message.error('请选择时间');
        return;
      }

      // 调用重新抄表接口
      apiV2MeterReadingExecutePost({
        areaIds: scadaIds || [],
        startDate: readingTime[0].format('YYYY-MM-DD'),
        endDate: readingTime[1].format('YYYY-MM-DD'),
      }).then(() => {
        message.success('重新抄表成功');
        form.resetFields();
        updateFn?.();
        onCancel();
      });
    } else {
      // 导出模式
      if (!readingTime?.[0] || !readingTime?.[1]) {
        message.error('请选择统计时间');
        return;
      }
      let startDate: string;
      let endDate: string;

      if (isMonth) {
        // 按月：传开始月的第一天到结束月的最后一天
        startDate = readingTime[0].startOf('month').format('YYYY-MM-DD');
        endDate = readingTime[1].endOf('month').format('YYYY-MM-DD');
      } else {
        startDate = readingTime[0].format('YYYY-MM-DD');
        endDate = readingTime[1].format('YYYY-MM-DD');
      }
      const params = {
        timestamp: Date.now(),
        areaIds: scadaIds || [],
        granularityType: granularityTypeFromForm || granularityType,
        startDate,
        endDate,
      };
      apiV2ExportCenterTaskAreaMeterReadingCreatePost(params).then(() => {
        form.resetFields();
        onCancel();
        let successModal: ReturnType<typeof Modal.success> | null = null;
        successModal = Modal.success({
          title: '导出任务创建成功',
          content: (
            <div style={{ whiteSpace: 'nowrap' }}>
              请前往报表中心下载导出的报表，
              <Button
                type="link"
                style={{ padding: 0, gap: 2 }}
                onClick={() => {
                  successModal?.destroy();
                  window.open('/report/export/list', '_blank');
                }}
              >
                点击前往
                <DoubleRightOutlined />
              </Button>
            </div>
          ),
        });
      });
    }
  };

  // 站房选项
  const scadaOptions = useMemo(
    () =>
      (areaListData?.list || []).map(item => ({
        label: item.name,
        value: item.id,
      })),
    [areaListData?.list]
  );

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={isReReadMode ? '手动重新抄表' : '导出'}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={isReReadMode ? '确定' : '确认导出'}
      cancelText="取消"
      width={600}
    >
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 16 }} onFinish={handleFinish}>
        <Form.Item label="站房" name="scadaIds" rules={[{ required: true, message: '请选择站房' }]}>
          <Select
            mode="multiple"
            placeholder="请选择站房"
            style={{ width: '100%' }}
            options={scadaOptions}
            loading={areaListLoading}
            maxTagCount="responsive"
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        {!isReReadMode && (
          <Form.Item label="选择时间" name="granularityType" rules={[{ required: true, message: '请选择时间颗粒度' }]}>
            <Radio.Group value={granularityType}>
              <Radio value="HOUR">按小时</Radio>
              <Radio value="DAY">按日</Radio>
              <Radio value="MONTH">按月</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {isReReadMode ? (
          <Form.Item label="选择时间" name="readingTime" rules={[{ required: true, message: '请选择时间' }]}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              picker="date"
              disabledDate={current => current && current > dayjs().endOf('day')}
              format="YYYY-MM-DD"
              showTime={false}
            />
          </Form.Item>
        ) : (
          <Form.Item label="统计时间" name="readingTime" rules={[{ required: true, message: '请选择统计时间' }]}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              picker={granularityType === 'MONTH' ? 'month' : 'date'}
              disabledDate={disabledDate}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ExportModal;
